import { Link as RouterLink, useParams } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import Link from '@mui/material/Link'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import SendIcon from '@mui/icons-material/Send'
import {
  useGetAuthoringComponentRevisionQuery,
  useGetSubmissionQuery,
  useJudgeSubmissionMutation,
  useResendSubmissionMutation,
} from '../../api/authoring'
import { problemMessage } from '../../api/baseApi'
import { formFields } from '../../model/form'
import { routes } from '../../model/routes'
import { Loading, QueryError } from '../../ui/QueryState'
import { SubmittedValues } from './SubmittedValues'
import { canResend, statusColour, statusLabel, wasDelivered, when } from './submissionStatus'
import { shown } from '../../api/useIntent'

/**
 * One submission, whole: every answer, what happened to it, the evidence,
 * and the two actions.
 *
 * **The two actions are not the same action.** A judgement records what a
 * person decided and changes nothing about what was sent — somebody
 * marking a delivered enquiry as spam cannot unsend the email. A resend
 * is the rescue, and it is what makes keeping a filtered row worth
 * anything at all.
 *
 * They compose rather than being one control: a submission judged genuine
 * that nobody ever received offers the resend beside it.
 */
export function Enquiry() {
  const { tenant = '', site = '', id = '' } = useParams()
  const query = useGetSubmissionQuery({ tenant, website: site, submission: id })
  const [judge, judging] = useJudgeSubmissionMutation()
  const [resend, resending] = useResendSubmissionMutation()

  const submission = query.data
  const form = useGetAuthoringComponentRevisionQuery(
    { tenant, id: submission?.form ?? '', revision: submission?.revision ?? 1 },
    { skip: !submission },
  )

  if (query.isLoading && !submission) return <Loading label="Reading the enquiry…" />
  if (query.error) return <QueryError error={query.error} title="Could not read the enquiry" />
  if (!submission) return null

  // The verdict decides the rendering, which is the point of having one.
  const inert = submission.status === 'spam' || submission.status === 'trapped'
  const fields = formFields(form.data)
  const values = submission.values ?? {}
  const busy = judging.isLoading || resending.isLoading
  const failure = judging.error ?? resending.error

  return (
    <Stack spacing={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" useFlexGap>
        <Box>
          <Typography variant="h1">Enquiry</Typography>
          <Typography color="text.secondary">
            Sent {when(submission.acceptedAt)} through{' '}
            {form.data?.label ?? 'a form on this site'}.
          </Typography>
        </Box>
        <Link component={RouterLink} to={routes.enquiries(tenant, site)}>
          Back to enquiries
        </Link>
      </Stack>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Chip label={statusLabel(submission.status)} color={statusColour(submission.status)} />
        {submission.judgement && (
          <Chip
            variant="outlined"
            label={`${submission.judgement.verdict === 'spam' ? 'Marked spam' : 'Marked genuine'} by ${submission.judgement.by}, ${when(submission.judgement.at)}`}
          />
        )}
      </Stack>

      {failure && <Alert severity="error">{problemMessage(failure)}</Alert>}

      {inert && (
        <Alert severity="warning">
          Nobody was emailed this. It is shown as plain text and nothing in it is a link, because a
          message that reached this screen got past nothing and you are signed in. If it is a real
          enquiry, send it.
        </Alert>
      )}

      {submission.status === 'failed' && (
        <Alert severity="error">
          Somebody sent this and the email could not be delivered. Send it again once the address on
          the form is right.
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <SubmittedValues values={values} fields={fields} inert={inert} />
      </Paper>

      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
        <Button
          variant="contained"
          startIcon={<SendIcon />}
          disabled={busy || !canResend(submission)}
          onClick={() => {
            resend({ tenant, website: site, submission: id }).catch(shown)
          }}
        >
          {wasDelivered(submission.status) ? 'Already emailed' : 'Email this to me'}
        </Button>
        <Button
          disabled={busy || submission.judgement?.verdict === 'ham'}
          onClick={() => {
            judge({ tenant, website: site, submission: id, judgeSubmission: { verdict: 'ham' } }).catch(shown)
          }}
        >
          This is genuine
        </Button>
        <Button
          disabled={busy || submission.judgement?.verdict === 'spam'}
          onClick={() => {
            judge({ tenant, website: site, submission: id, judgeSubmission: { verdict: 'spam' } }).catch(shown)
          }}
        >
          This is spam
        </Button>
      </Stack>

      {/* Marking something genuine does not send it, and the screen says
          so rather than letting a reader assume a judgement did
          something it cannot do. */}
      {!canResend(submission) && !wasDelivered(submission.status) && (
        <Typography color="text.secondary">
          The answers to this one were not kept, so there is nothing left to send.
        </Typography>
      )}

      <Paper sx={{ p: 3 }}>
        <Typography variant="h3" gutterBottom>
          How it arrived
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Stack spacing={1.5}>
          <Fact label="Accepted" value={when(submission.observed.at)} />
          {/* Two readings, kept apart, because they are two different
              claims. What the platform read off the connection is the
              caller that relayed the submission — not the visitor —
              and what the caller reported is what it says about
              somebody the platform never met. */}
          <Fact label="Received from" value={submission.observed.ip} hint="the connection this arrived on" />
          <Fact label="Visitor address" value={submission.reported?.ip} hint="as reported, not verified" />
          <Fact label="Visitor browser" value={submission.reported?.userAgent} hint="as reported, not verified" />
          <Fact label="Submitted from" value={submission.reported?.referrer} hint="as reported, not verified" />
          {submission.reported?.elapsed !== undefined && (
            <Fact label="Time on the form" value={`${submission.reported.elapsed}s`} hint="as reported, not verified" />
          )}
          {submission.expiresAt && (
            <Fact label="Deleted from here" value={when(submission.expiresAt)} hint="the email you were sent is the copy that lasts" />
          )}
        </Stack>
      </Paper>
    </Stack>
  )
}

/**
 * One fact about the arrival, as plain text and never as a link.
 *
 * Every value here came off a request, and half of them are what a caller
 * *said* rather than what the platform saw. A referrer rendered as an
 * anchor would be the same attack the inert rendering refuses, arriving
 * through the evidence panel instead of through the answers.
 */
function Fact({ label, value, hint }: { label: string; value?: string; hint?: string }) {
  if (!value) return null
  return (
    <Box>
      <Typography variant="overline" color="text.secondary">
        {label}
        {hint && <Typography component="span" variant="caption" color="text.disabled"> — {hint}</Typography>}
      </Typography>
      <Typography sx={{ wordBreak: 'break-all' }}>{value}</Typography>
    </Box>
  )
}
